import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
} from '@angular/core';

import { DatePipe } from '@angular/common';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { DEEP_AGENT_BASE_URL } from '@boldpenguin/emperor-features';
import {
  EmperorLoaderComponent,
  EmperorSpinnerModule,
  WEB_VIEWER_OPTIONS,
} from '@boldpenguin/emperor-presentational';
import { ENVIRONMENT } from '@boldpenguin/emperor-services';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MarkdownModule } from 'ngx-markdown';
import { SortablejsModule } from 'ngx-sortablejs';
import { provideNgxWebstorage, withLocalStorage } from 'ngx-webstorage';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppErrorHandler } from './app.error';
import { CoreModule } from './core/core.module';
import { EnrollmentsService } from './core/services/enrollments-service/enrollments.service';
import { IntercomService } from './core/services/intercom-service/intercom.service';
import { NavigationModule } from './navigation/navigation.module';
import { ComponentTrackingEffects } from './root-store/component-tracking/component-tracking.effects';
import { RootStateEffects } from './root-store/root.effects';
import { RootReducer } from './root-store/root.reducers';
import { DebugDrawerComponent } from './shared/components/debug-drawer/debug-drawer.component';
import { InProgressPanelComponent } from './shared/components/in-progress-processes/in-progress-panel.component';
import { KonamiDirective } from './shared/directive/konami/konami.directive';
import { httpInterceptorProviders } from './shared/http-interceptors';
import { SharedModule } from './shared/shared.module';

const initializeIntercomFactory = (intercomService: IntercomService) => () =>
  intercomService.loadIntercomScript();

@NgModule({
  declarations: [AppComponent, KonamiDirective],
  imports: [
    BrowserModule,
    CoreModule,
    SharedModule,
    ScrollingModule,
    NavigationModule,
    MatSidenavModule,
    DebugDrawerComponent,
    InProgressPanelComponent,
    EmperorSpinnerModule,
    StoreModule.forRoot({ router: routerReducer, root: RootReducer }, {}),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([RootStateEffects, ComponentTrackingEffects]),
    SortablejsModule.forRoot({ animation: 150 }),
    BrowserAnimationsModule,
    MarkdownModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
    }),
    EmperorLoaderComponent,
  ],
  providers: [
    provideNgxWebstorage(withLocalStorage()),
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideAppInitializer(() =>
      inject(AppErrorHandler).handleErrorsFromExceptionsBridge(),
    ),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', hideRequiredMarker: true },
    },
    {
      provide: WEB_VIEWER_OPTIONS,
      useValue: {
        path: '/assets/pdftron/lib',
        licenseKey:
          'Bold Penguin Inc   formerly ClaimKit LLC :OEM:RiskGenius::B+:AMS(20281103):E0A5F3AD04C7B60A2360B13AC9A2537860615FB56C364A737C54B6DA6A2C4CF66ABAB6F5C7',
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeIntercomFactory,
      deps: [IntercomService],
      multi: true,
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: DEEP_AGENT_BASE_URL,
      useValue: environment.deepAgentBaseUrl,
    },
    EnrollmentsService,
    httpInterceptorProviders,
    DatePipe,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
